/**
 * `<SettingsFeatureFlagsRoute>` — `/w/:slug/feature-flags` (B3).
 *
 * Lists every feature flag the caller may see (the API hides `internalOnly`
 * flags from non-platform-staff) with its effective value + source, and lets
 * an org manager toggle the workspace-scope override. Boolean flags render a
 * switch; turning it off clears the override (falls back to the default).
 */

import { useWorkspace } from "@cometchat-ai/auth/client";
import { Badge, Switch, toast } from "@cometchat-ai/ui-dashboard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Flag } from "../components/icons";
import { useTranslation } from "react-i18next";

import { ErrorState } from "../components/error-state";
import { ListSkeleton } from "../components/list-skeleton";
import { PageShell } from "../components/page-shell";
import { SettingsCard } from "../components/settings-card";
import { type AdminFlag, clearFlag, flagsKeys, listFlags, setFlag } from "../settings/api";

export function SettingsFeatureFlagsRoute() {
  const { t } = useTranslation("workspace");
  useWorkspace();
  const queryClient = useQueryClient();

  const flagsQuery = useQuery({ queryKey: flagsKeys.list(), queryFn: listFlags });

  const toggle = useMutation({
    mutationFn: ({ flag, next }: { flag: AdminFlag; next: boolean }) =>
      next
        ? setFlag(flag.key, { value: true, scope: "workspace" })
        : clearFlag(flag.key, "workspace"),
    onSuccess: () => {
      toast.success(t("featureFlags.updatedToast"));
      void queryClient.invalidateQueries({ queryKey: flagsKeys.list() });
    },
    onError: () => toast.error(t("featureFlags.errorToast")),
  });

  const flags = flagsQuery.data?.flags ?? [];

  return (
    <PageShell className="p-0" data-testid="settings-feature-flags">
      <div className="mx-auto w-full max-w-6xl px-6 py-6">
        {flagsQuery.isPending ? (
          <ListSkeleton rows={4} itemClassName="h-10 w-full" className="space-y-2" />
        ) : flagsQuery.error ? (
          <ErrorState message={t("featureFlags.loadError")} onRetry={flagsQuery.refetch} />
        ) : flags.length === 0 ? (
          <SettingsCard icon={Flag} title={t("featureFlags.title")}>
            <p className="text-[13px] text-muted-foreground">{t("featureFlags.empty")}</p>
          </SettingsCard>
        ) : (
          <SettingsCard icon={Flag} title={t("featureFlags.title")}>
            <ul className="-mx-5 -mb-4 mt-1 divide-y divide-border">
              {flags.map((flag) => (
                <li
                  key={flag.key}
                  className="flex items-center justify-between gap-4 px-5 py-3.5"
                  data-testid={`flag-row-${flag.key}`}
                >
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-medium text-foreground">{flag.key}</span>
                      {flag.internalOnly ? (
                        <Badge variant="outline">{t("featureFlags.internalBadge")}</Badge>
                      ) : null}
                      <Badge variant="secondary">
                        {t(
                          flag.source === "workspace"
                            ? "featureFlags.sourceWorkspace"
                            : flag.source === "org"
                              ? "featureFlags.sourceOrg"
                              : "featureFlags.sourceDefault",
                        )}
                      </Badge>
                    </div>
                    <p className="text-[13px] text-muted-foreground">{flag.description}</p>
                  </div>
                  {flag.kind === "boolean" ? (
                    <Switch
                      checked={flag.value === true}
                      disabled={toggle.isPending}
                      onCheckedChange={(next) => toggle.mutate({ flag, next })}
                      aria-label={flag.key}
                      data-testid={`flag-switch-${flag.key}`}
                    />
                  ) : (
                    <span className="text-[13px] text-muted-foreground">{String(flag.value)}</span>
                  )}
                </li>
              ))}
            </ul>
          </SettingsCard>
        )}
      </div>
    </PageShell>
  );
}
